import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDownToLine, ArrowLeftRight, ArrowUpFromLine } from 'lucide-react'
import { useMe } from '@/lib/auth'
import { useTRPC } from '@/lib/trpc'
import { usePortfolio } from '@/lib/portfolio'
import { primaryWallet, type PositionSnapshot, type StrategyTemplate } from '@/lib/api-types'
import { LAMPORTS_PER_SOL } from '@/lib/format'
import { WalletSetup } from '@/components/wallet-setup'
import { HoldingsTable } from '@/components/holdings-table'
import { SkeletonRows } from '@/components/skeleton-rows'
import { HoldingsSettings } from '@/components/holdings-settings'
import { CardFigure, HEADER_BTN, HEADER_ICON } from '@/components/card-header'
import { PositionsCard } from '@/components/positions-card'
import { PositionSellModal } from '@/components/position-sell-modal'
import { TransactionsTable } from '@/components/transactions-table'
import { StrategiesSection } from '@/components/strategies-section'
import { StrategyWizard } from '@/components/strategy-wizard'
import { StrategyDetailModal } from '@/components/strategy-detail-modal'
import { DepositModal } from '@/components/deposit-modal'
import { WithdrawModal, type TrackedWithdrawal } from '@/components/withdraw-modal'
import { SwapModal, type TrackedSwap } from '@/components/swap-modal'
import { KrakenCard } from '@/components/kraken-card'
import { KrakenSellModal, type KrakenSellAsset } from '@/components/kraken-sell-modal'
import { KrakenWithdrawModal } from '@/components/kraken-withdraw-modal'
import { Spinner } from '@/components/spinner'
import { TokenActions } from '@/components/token-actions'
import type { PickedToken } from '@/lib/picked-token'
import { usePoolMode, useCustody } from '@/lib/pool'

type Action = 'deposit' | 'withdraw' | 'swap' | null


/** The one home screen: wallet, holdings, transactions, strategies. */
export function DashboardPage() {
  const trpc = useTRPC()
  const { user } = useMe()
  const wallet = primaryWallet(user)
  const ready = Boolean(wallet && user?.walletSetupComplete)
  const [params, setParams] = useSearchParams()
  const [action, setAction] = useState<Action>(null)
  const [depositId, setDepositId] = useState<string | null>(null)
  /** a flow already running, opened from its chip in the in-progress bar (John, Oct 31 2026) */
  const [trackSwap, setTrackSwap] = useState<TrackedSwap | null>(null)
  const [trackWithdrawal, setTrackWithdrawal] = useState<TrackedWithdrawal | null>(null)
  /** the holdings row that was tapped, offering its actions */
  const [focus, setFocus] = useState<PickedToken | null>(null)
  /** the token a flow opens with: tapped row's token, or none from the header buttons */
  const [initial, setInitial] = useState<PickedToken | null>(null)
  const [selling, setSelling] = useState<PositionSnapshot | null>(null)
  const [creating, setCreating] = useState(false)
  /** the holdings row's token a new strategy starts with (John, Oct 31 2026) */
  const [strategyToken, setStrategyToken] = useState<PickedToken | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  /** the strategy a new one copies (John, Nov 3 2026): the wizard opens filled in with it */
  const [strategyTemplate, setStrategyTemplate] = useState<StrategyTemplate | null>(null)
  const duplicateStrategy = (strategy: StrategyTemplate) => {
    setSelectedId(null)
    setStrategyTemplate(strategy)
    setCreating(true)
  }
  /** the asset on Kraken being sold for USD (John, Nov 1 2026) */
  const [krakenAsset, setKrakenAsset] = useState<KrakenSellAsset | null>(null)
  /** USD on Kraken going to a bank (John, Nov 2 2026) */
  const [krakenWithdraw, setKrakenWithdraw] = useState(false)
  useEffect(() => {
    const requested = params.get('action')
    if (requested === 'deposit' || requested === 'withdraw' || requested === 'swap') setAction(requested)
    if (requested === 'deposit') setDepositId(params.get('id'))
    else if (requested === 'send') setAction('withdraw') // older links
    if (requested === 'swap') {
      const position = params.get('position')
      const swap = params.get('swap')
      const conversion = params.get('conversion')
      setTrackSwap(position ? { kind: 'position', positionId: position, mode: params.get('mode') === 'sell' ? 'sell' : 'open' } : swap ? { kind: 'swap', swapId: swap } : conversion ? { kind: 'conversion', id: conversion } : null)
    }
    if (requested === 'withdraw') {
      const vault = params.get('withdrawal')
      const privateId = params.get('private')
      const transfer = params.get('transfer')
      setTrackWithdrawal(vault ? { kind: 'vault', id: vault } : privateId ? { kind: 'private', id: privateId } : transfer ? { kind: 'transfer', transferId: transfer } : null)
    }
    if (params.get('create')) setCreating(true)
    // a strategy opened from the in-progress bar shows here, on the page the user is on (John, Nov 2 2026: a chip used to
    // move him to the Strategies tab and leave him there once he closed the detail)
    const strategy = params.get('strategy')
    if (strategy) setSelectedId(strategy)
    if (requested || params.get('create') || strategy) setParams({}, { replace: true })
  }, [params, setParams])
  const portfolio = usePortfolio(wallet, ready)
  const strategies = useQuery({ ...trpc.strategy.list.queryOptions(), enabled: ready, refetchInterval: 15_000 })
  // the deposit address is read ahead, so the Deposit dialog opens with it already on screen
  const queryClient = useQueryClient()
  const poolMode = usePoolMode()
  const { custody, exchange, dispenceVault } = useCustody()
  const walletId = wallet?.id
  useEffect(() => {
    if (!ready || !walletId || !poolMode || !exchange) return
    void queryClient.prefetchQuery({ ...trpc.vault.openPoolDeposit.queryOptions({ walletId, asset: dispenceVault ? 'token' : 'SOL', platform: custody }), staleTime: 30_000 })
    // the account's exchange's rails too (John, Nov 4 2026: the dialogs were slow to settle on a fresh page): the other
    // exchanges' come when a dialog opens
    for (const symbol of ['SOL', 'USDC', 'USDT', 'ETH', 'BNB'] as const) {
      for (const rail of ['deposit', 'withdraw'] as const) {
        void queryClient.prefetchQuery({ ...trpc.vault.networks.queryOptions({ platform: custody, symbol, rail }), staleTime: 5 * 60_000 })
      }
    }
  }, [ready, walletId, poolMode, exchange, custody, dispenceVault, queryClient, trpc])

  function openAction(kind: Exclude<Action, null>, token: PickedToken | null = null) {
    setInitial(token)
    setFocus(null)
    setTrackSwap(null)
    setTrackWithdrawal(null)
    setAction(kind)
  }

  if (!ready || !wallet) return <WalletSetup />
  const solLamports = portfolio.data?.native?.solana?.balance
  const sol = solLamports !== undefined ? Number(solLamports) / LAMPORTS_PER_SOL : undefined
  const stuck = (portfolio.data?.positions ?? []).filter((p) => p.status === 'failed' || p.status === 'stalled')
  const loading = portfolio.isPending
  const list = strategies.data ?? []
  // wallet tokens plus what the vault holds for this account, as one list
  const holdings = [...(portfolio.data?.tokens ?? []), ...(portfolio.data?.vault ?? [])]
  // a platform could not be read (Sonar down, the vault's last scan): the figures still show, marked (Nov 1 2026: every platform at once)
  const poolStale = Boolean(portfolio.data?.poolUnavailable)

  return (
    <div className="grid gap-6 *:min-w-0">
      {/* Holdings: the portfolio total and the three flows sit in the table's top row (John, Nov 3 2026: the separate
          balance card with the SOL line went); the buttons are icons alone below lg (Nov 3: at sm the words still
          ran over the total in a half-width window), as the Strategies header does on phones. The list's filter
          control sits right after the total, not among the flows (John, Nov 4 2026); the row is positioned so its
          popover can hang from the header's left edge on phones. The total's skeleton is the width of the figure
          it stands in for (Nov 4: a 96px one pushed the buttons onto a second line on every refresh) */}
      <section className="card">
        <div className="relative flex flex-wrap items-center justify-between gap-x-2 gap-y-2 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-x-2 sm:gap-x-3">
            <h2 className="card-title">Holdings</h2>
            <span className="card-divider" aria-hidden="true" />
            {loading ? <span className="skeleton inline-block h-5 w-16 sm:h-6 sm:w-28" /> : <CardFigure usd={portfolio.data?.totalValueUsd} />}
            <HoldingsSettings />
            {poolStale ? <span className="hidden text-xs text-muted lg:inline">Some balances may be out of date</span> : null}
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
            <button type="button" className={`${HEADER_BTN} btn-primary`} onClick={() => openAction('swap')} aria-label="Swap" title="Swap">
              <ArrowLeftRight className={HEADER_ICON} />
              <span className="hidden lg:inline">Swap</span>
            </button>
            <button type="button" className={`${HEADER_BTN} btn-secondary`} onClick={() => openAction('deposit')} aria-label="Deposit" title="Deposit">
              <ArrowDownToLine className={HEADER_ICON} />
              <span className="hidden lg:inline">Deposit</span>
            </button>
            <button type="button" className={`${HEADER_BTN} btn-secondary`} onClick={() => openAction('withdraw')} aria-label="Withdraw" title="Withdraw">
              <ArrowUpFromLine className={HEADER_ICON} />
              <span className="hidden lg:inline">Withdraw</span>
            </button>
          </div>
        </div>
        {/* the header has no room for the note beside the icons: it gets its own line there */}
        {poolStale ? <p className="px-4 pb-2 text-xs text-muted sm:px-6 lg:hidden">Some balances may be out of date</p> : null}
        <div className="border-t border-line">
          {loading ? (
            <SkeletonRows rows={3} columns={5} />
          ) : (
            <HoldingsTable tokens={holdings} onSelect={setFocus} />
          )}
        </div>
      </section>

      {/* what sits on Kraken, sold for USD from here (John, Nov 1 2026) */}
      <KrakenCard onSell={setKrakenAsset} onWithdraw={() => setKrakenWithdraw(true)} />

      {/* trade wallets are run for the account (John, Oct 30 2026: the strategy view and the tokens table say it all); only one that stopped with funds in it is shown, so they can be returned */}
      {stuck.length > 0 ? <PositionsCard positions={stuck} onSell={setSelling} /> : null}

      {/* Strategies as rows; finished ones folded here, open on the Strategies tab */}
      <StrategiesSection strategies={list} holdings={holdings} pending={strategies.isPending} failed={strategies.isError} completedOpen={false} onCreate={() => setCreating(true)} onSelect={setSelectedId} onDuplicate={duplicateStrategy} />

      <TransactionsTable wallet={wallet} holdings={holdings} />

      <TokenActions
        token={focus}
        onClose={() => setFocus(null)}
        onAction={(kind, token) => {
          if (kind !== 'strategy') return openAction(kind, token)
          setFocus(null)
          setStrategyToken(token)
          setCreating(true)
        }}
      />
      <DepositModal open={action === 'deposit'} onClose={() => { setAction(null); setDepositId(null) }} wallet={wallet} initialId={depositId} initialToken={initial} />
      <WithdrawModal open={action === 'withdraw'} onClose={() => { setAction(null); setTrackWithdrawal(null) }} wallet={wallet} holdings={holdings} initialToken={initial} track={trackWithdrawal} />
      <SwapModal open={action === 'swap'} onClose={() => { setAction(null); setTrackSwap(null) }} wallet={wallet} holdings={holdings} initialPay={initial} track={trackSwap} />
      <PositionSellModal position={selling} onClose={() => setSelling(null)} />
      <KrakenSellModal asset={krakenAsset} onClose={() => setKrakenAsset(null)} />
      <KrakenWithdrawModal open={krakenWithdraw} onClose={() => setKrakenWithdraw(false)} />
      <StrategyWizard open={creating} onClose={() => { setCreating(false); setStrategyToken(null); setStrategyTemplate(null) }} wallet={wallet} holdings={holdings} solBalance={sol} initialToken={strategyToken} template={strategyTemplate} />
      <StrategyDetailModal id={selectedId} onClose={() => setSelectedId(null)} onDuplicate={duplicateStrategy} />
      {portfolio.isError ? <p className="text-sm text-negative">Balances could not be loaded. <Spinner className="ml-2" /></p> : null}
    </div>
  )
}
